/**
 * PATH: apps/sam/src/features/email/lib/colorPalettes.ts
 * FILE_ID: SAM-EMAIL-LIB-001
 * PURPOSE: Deterministic colour palettes for the inbox row's mailbox
 *          attribution chip and job pill. Same input always returns
 *          the same colour so a user can pattern-match "that's a
 *          jake@ email" / "that's job #56" at a glance.
 * WHY:     Multiple connected mailboxes feed the same inbox; without
 *          colour hints it's impossible to tell whose mailbox a row
 *          came from at a glance. Same problem with jobs — `#56`
 *          appears as plain mono text everywhere today.
 * FLOW:
 *   ┌─────────────┐   ┌────────────┐   ┌────────────────────┐
 *   │ key (string │ → │  djb2 hash │ → │  palette[hash %    │
 *   │ or number)  │   │            │   │  palette.length]   │
 *   └─────────────┘   └────────────┘   └────────────────────┘
 *
 * NEUTRALITY:
 *   - These are Tailwind class strings, not raw hex codes, so dark
 *     mode is handled automatically. Adding a new palette member is
 *     a one-line tuple addition.
 */

/**
 * Tiny, deterministic non-cryptographic hash (djb2). Used purely for
 * palette indexing — same input always returns the same numeric
 * bucket across page loads, devices, and renders. We don't need
 * collision resistance, just stability.
 */
function djb2(value: string): number {
  let h = 5381
  for (let i = 0; i < value.length; i++) {
    h = ((h << 5) + h + value.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

/**
 * Resolved chip styling. ``bg`` is a soft tinted background, ``text``
 * is a high-contrast accent for the label, ``border`` traces the
 * pill, ``dot`` is the saturated swatch used in legend / palette
 * indicators. All four play nicely with both light and dark themes.
 */
export interface ChipStyle {
  bg: string
  text: string
  border: string
  dot: string
}

/**
 * 8-colour palette for mailbox attribution chips. Each tuple is a
 * Tailwind colour family chosen to read well on both ``bg-background``
 * and ``bg-muted/30``. We deliberately skip blue (reserved for the
 * legacy job pill) and yellow/amber (reserved for the unread / queued
 * statuses) so palette collisions never overlap with semantic colour.
 */
const MAILBOX_PALETTE: ChipStyle[] = [
  { bg: 'bg-rose-100 dark:bg-rose-900/30',     text: 'text-rose-700 dark:text-rose-200',     border: 'border-rose-300/50 dark:border-rose-700/50',   dot: 'bg-rose-500'   },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-200', border: 'border-emerald-300/50 dark:border-emerald-700/50', dot: 'bg-emerald-500' },
  { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-200', border: 'border-violet-300/50 dark:border-violet-700/50', dot: 'bg-violet-500' },
  { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-200', border: 'border-orange-300/50 dark:border-orange-700/50', dot: 'bg-orange-500' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/30',     text: 'text-cyan-700 dark:text-cyan-200',     border: 'border-cyan-300/50 dark:border-cyan-700/50',   dot: 'bg-cyan-500'   },
  { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30', text: 'text-fuchsia-700 dark:text-fuchsia-200', border: 'border-fuchsia-300/50 dark:border-fuchsia-700/50', dot: 'bg-fuchsia-500' },
  { bg: 'bg-lime-100 dark:bg-lime-900/30',     text: 'text-lime-800 dark:text-lime-200',     border: 'border-lime-300/50 dark:border-lime-700/50',   dot: 'bg-lime-500'   },
  { bg: 'bg-teal-100 dark:bg-teal-900/30',     text: 'text-teal-700 dark:text-teal-200',     border: 'border-teal-300/50 dark:border-teal-700/50',   dot: 'bg-teal-500'   },
]

/**
 * 12-colour palette for job pills. Wider than the mailbox palette
 * because orgs typically have many more jobs than connected mailboxes
 * — more buckets means fewer perceived collisions. Pulls in blue
 * (the legacy default) at a fixed slot so jobs hashed there look
 * familiar.
 */
const JOB_PALETTE: ChipStyle[] = [
  { bg: 'bg-blue-100 dark:bg-blue-900/30',         text: 'text-blue-700 dark:text-blue-200',         border: 'border-blue-300/50 dark:border-blue-700/50',         dot: 'bg-blue-500'         },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/30',   text: 'text-emerald-700 dark:text-emerald-200',   border: 'border-emerald-300/50 dark:border-emerald-700/50',   dot: 'bg-emerald-500'      },
  { bg: 'bg-violet-100 dark:bg-violet-900/30',     text: 'text-violet-700 dark:text-violet-200',     border: 'border-violet-300/50 dark:border-violet-700/50',     dot: 'bg-violet-500'       },
  { bg: 'bg-orange-100 dark:bg-orange-900/30',     text: 'text-orange-700 dark:text-orange-200',     border: 'border-orange-300/50 dark:border-orange-700/50',     dot: 'bg-orange-500'       },
  { bg: 'bg-rose-100 dark:bg-rose-900/30',         text: 'text-rose-700 dark:text-rose-200',         border: 'border-rose-300/50 dark:border-rose-700/50',         dot: 'bg-rose-500'         },
  { bg: 'bg-amber-100 dark:bg-amber-900/30',       text: 'text-amber-700 dark:text-amber-200',       border: 'border-amber-300/50 dark:border-amber-700/50',       dot: 'bg-amber-500'        },
  { bg: 'bg-teal-100 dark:bg-teal-900/30',         text: 'text-teal-700 dark:text-teal-200',         border: 'border-teal-300/50 dark:border-teal-700/50',         dot: 'bg-teal-500'         },
  { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30',   text: 'text-fuchsia-700 dark:text-fuchsia-200',   border: 'border-fuchsia-300/50 dark:border-fuchsia-700/50',   dot: 'bg-fuchsia-500'      },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/30',         text: 'text-cyan-700 dark:text-cyan-200',         border: 'border-cyan-300/50 dark:border-cyan-700/50',         dot: 'bg-cyan-500'         },
  { bg: 'bg-lime-100 dark:bg-lime-900/30',         text: 'text-lime-800 dark:text-lime-200',         border: 'border-lime-300/50 dark:border-lime-700/50',         dot: 'bg-lime-500'         },
  { bg: 'bg-indigo-100 dark:bg-indigo-900/30',     text: 'text-indigo-700 dark:text-indigo-200',     border: 'border-indigo-300/50 dark:border-indigo-700/50',     dot: 'bg-indigo-500'       },
  { bg: 'bg-sky-100 dark:bg-sky-900/30',           text: 'text-sky-700 dark:text-sky-200',           border: 'border-sky-300/50 dark:border-sky-700/50',           dot: 'bg-sky-500'          },
]

/** Neutral fallback for null / missing keys (e.g. legacy rows without
 *  ``synced_by_user_id``). Never gets picked by the hash function — only
 *  returned explicitly when the caller passes an empty / nullish key. */
const NEUTRAL_STYLE: ChipStyle = {
  bg: 'bg-muted',
  text: 'text-muted-foreground',
  border: 'border-border',
  dot: 'bg-muted-foreground/50',
}

/**
 * Resolve a stable colour for a mailbox identified by its email
 * address (lowercased to make casing irrelevant). Returns a neutral
 * "Unknown" style for empty / nullish input so the row still renders
 * gracefully on legacy pre-stamp rows.
 */
export function mailboxColor(email: string | null | undefined): ChipStyle {
  if (!email) return NEUTRAL_STYLE
  const idx = djb2(email.toLowerCase()) % MAILBOX_PALETTE.length
  return MAILBOX_PALETTE[idx]
}

/**
 * Resolve a stable colour for a job by integer id. Returns the
 * neutral fallback for null / undefined.
 */
export function jobColor(jobId: number | null | undefined): ChipStyle {
  if (jobId == null) return NEUTRAL_STYLE
  const idx = djb2(String(jobId)) % JOB_PALETTE.length
  return JOB_PALETTE[idx]
}

/** Convenience: combine bg + text into a single Tailwind class string
 *  for the EmailRow's ``jobPillClass`` prop. Border isn't included
 *  because the existing pill uses a fully-rounded shape that doesn't
 *  benefit from a border ring. */
export function jobPillClassFor(jobId: number | null | undefined): string {
  const c = jobColor(jobId)
  return `${c.bg} ${c.text}`
}

/**
 * Raw-hex palette for the inbox row's BOARD FRAME (the rounded accent
 * rail + tint). Unlike the Tailwind palettes above, the frame is drawn
 * with inline ``color-mix`` so it needs concrete CSS colours, not class
 * strings. These are the 600-ish swatches of the same families so the
 * frame reads as a saturated spine in light mode and still shows
 * through the dark-mode tint. Mid-saturation on purpose — a board frame
 * is ambient context, not a call-to-action.
 */
const BOARD_HEX_PALETTE = [
  '#2563eb', // blue
  '#059669', // emerald
  '#7c3aed', // violet
  '#ea580c', // orange
  '#e11d48', // rose
  '#0d9488', // teal
  '#c026d3', // fuchsia
  '#0891b2', // cyan
  '#4f46e5', // indigo
  '#65a30d', // lime
]

/**
 * Resolve the board frame colour. Prefers the board's own
 * ``board_color`` when the backend has one; otherwise falls back to a
 * stable hashed swatch keyed on the board id so the same board always
 * frames its emails the same colour across renders / sessions.
 */
export function boardFrameColor(
  boardColor: string | null | undefined,
  boardId: string | null | undefined,
): string {
  const explicit = (boardColor || '').trim()
  if (explicit) return explicit
  const seed = (boardId || '').trim()
  if (!seed) return BOARD_HEX_PALETTE[0]
  return BOARD_HEX_PALETTE[djb2(seed) % BOARD_HEX_PALETTE.length]
}

/**
 * Resolve a concrete hex frame colour for a JOB. Used as the fallback
 * spine when an email isn't on a Kanban board yet but IS linked to a
 * job — so the inbox still reads as workflow clusters (job-coloured
 * rails) instead of an undifferentiated list. Keyed on the job id so a
 * given job always frames its emails the same colour, and drawn from
 * the same hex palette as the board frame so the two visual languages
 * are consistent (board wins when both are present — see InboxPage).
 */
export function jobFrameColor(jobId: number | null | undefined): string {
  if (jobId == null) return BOARD_HEX_PALETTE[0]
  return BOARD_HEX_PALETTE[djb2(String(jobId)) % BOARD_HEX_PALETTE.length]
}
